/**
 * Registry of askFinz vertical apps. Single source of truth for the
 * marketing site — used by /apps, the home-page showcase, sitemap, etc.
 *
 * Each entry carries a `status` flag so the marketing site can be honest
 * about what's shipping vs what's on the roadmap. Verified against the
 * actual code under each `*.askfinz.ai/` directory (see project memory
 * `project_marketing_site.md` for the audit trail).
 *
 * Copy is intentionally non-technical and verified-true. One short
 * sentence per app.
 */

import { CARRIER_REGIONS } from "./shop-carriers";

/* Counted from the carrier registry rather than typed. This description flows into /apps/shop and
   into llms.txt, so the literal "30+" was being republished in two more places than the shop page
   itself — and it implied at least 30 when the registry holds 37. */
const CARRIER_TOTAL = CARRIER_REGIONS.reduce((n, r) => n + r.carriers.length, 0);

export type AppStatus = "shipping" | "beta" | "soon";

export type AppEntry = {
  slug: string;
  name: string;
  domain: string;
  tagline: string;
  description: string;
  category: "core" | "vertical" | "tools";
  status: AppStatus;
  /** SERP title override. The `tagline` is written to sound good on the page; where that runs
   *  past the ~60 characters a result page shows (brand suffix included), this supplies a short,
   *  keyword-leading alternative used ONLY in <title>. Omit it and the tagline is used. */
  seoTitle?: string;
  /** Two-letter monogram glyph rendered in the hero tile. Defaults to first
   *  two letters of the app name when omitted. */
  accent?: string;
  /** What's actually working today — populated for shipping/beta workspaces. */
  featuresShipping?: string[];
  /** What's planned next — populated for roadmap (soon) workspaces. */
  featuresPlanned?: string[];
  /** Surfaces this workspace is available on. Drives the "Available on"
   *  footer block on /apps/[slug]. Defaults to ["cloud"]. */
  availableOn?: ("cloud" | "extension" | "desktop")[];
  /** When true, this is an internal admin/observability surface that is not
   *  marketed as a public product. Renders an "internal" disclaimer. */
  internal?: boolean;
};

export const APPS: AppEntry[] = [
  // ─── Core (everyday surfaces) ───────────────────────────────────────────
  {
    slug: "chat",
    seoTitle: "AI chat with every model: GPT, Claude, Gemini",
    name: "Chat",
    domain: "chat.askfinz.ai",
    tagline: "One conversation, every model.",
    description:
      "Switch between AI minds in a single thread. Bring files, keep history.",
    category: "core",
    status: "shipping",
    accent: "Ch",
    availableOn: ["cloud", "extension", "desktop"],
    featuresShipping: [
      "Switch models mid-conversation — pick the right AI for each question without starting over",
      "Upload files, images, PDFs and spreadsheets — the model reads them and answers in context",
      "See cost and latency per message — know what you're spending and how long each answer took",
      "Thread history that persists — every conversation is saved and resumable",
      "Model routing that suggests — picks the right model for your question type, or switch by hand",
      "Markdown, code blocks and syntax highlighting — answers render cleanly with proper formatting",
    ],
  },
  {
    slug: "research",
    seoTitle: "AI research assistant with cited sources",
    name: "Research",
    domain: "research.askfinz.ai",
    tagline: "Studies that cite their sources.",
    description:
      "Notebooks that read, synthesise and reference. Branch, version, merge — turn a question into a defensible answer.",
    category: "core",
    status: "shipping",
    accent: "Re",
    availableOn: ["cloud", "extension", "desktop"],
    featuresShipping: [
      "Branch research paths — explore multiple angles on the same question, merge the best findings later",
      "Citations as first-class elements — every claim links back to its source, inline and verifiable",
      "Version control for ideas — see how your thinking evolved, roll back to earlier drafts",
      "Merge findings without losing history — combine two research branches, keep the provenance intact",
      "Multi-format output — write prose, build tables, generate slides from the same research base",
      "Collaborative notebooks — share with teammates, they can branch and contribute without overwriting your work",
      "Source ingestion from web, PDFs and papers — drop a URL or file, the system reads and indexes it",
    ],
  },
  {
    slug: "code",
    seoTitle: "AI coding workspace for whole projects",
    name: "Code",
    domain: "code.askfinz.ai",
    tagline: "A workspace for projects, not just files.",
    description:
      "Projects, deployments, templates, a pair-programmer that reads the room. A home for the work between commits.",
    category: "core",
    status: "shipping",
    accent: "Co",
    availableOn: ["cloud", "desktop"],
    featuresShipping: [
      "Full-featured editor in the browser — syntax highlighting, multi-file editing, search and replace across the project",
      "Run and test without leaving the workspace — integrated terminal, live preview, debug output in one view",
      "Git integration built in — commit, push, pull and branch without switching tools",
      "Deploy from the editor — connect to hosting platforms, ship changes with one click",
      "AI pair programmer that reads your codebase — ask questions about your project, get answers that reference your actual code",
      "Project templates and scaffolding — start new projects from proven patterns, customized to your stack",
      "Collaborative editing — share a project link, work together in real-time with presence indicators",
      "Code review and inline comments — annotate files, suggest changes, discuss before merging",
    ],
  },
  {
    slug: "news",
    seoTitle: "AI news briefing: today, in one summary",
    name: "News",
    domain: "news.askfinz.ai",
    tagline: "Today, in one briefing.",
    description:
      "Many sources, deduplicated and clustered, with a daily audio briefing you can listen to.",
    category: "core",
    status: "shipping",
    accent: "Ne",
    availableOn: ["cloud", "extension", "desktop"],
    featuresShipping: [
      "One briefing across dozens of sources — deduplicated, clustered by story, ranked by what matters",
      "Daily audio briefing on demand — listen to the day's news in your voice preference, generated fresh each morning",
      "Source transparency — every story shows which outlets covered it, compare angles side by side",
    ],
    featuresPlanned: [
      "Live signals — watch stories break and track coverage as it evolves",
      "Custom feeds and alerts — follow specific beats by topic or keyword",
      "Save and annotate — bookmark stories, add notes, scrub the archive by date",
    ],
  },
  {
    slug: "search",
    seoTitle: "AI search engine with sources and citations",
    name: "Search",
    domain: "search.askfinz.ai",
    tagline: "A search engine for humans, agents and LLMs.",
    description:
      "askFinz's own search engine. Built so a human, an agent or a model can ask the same question and get back something they can each use.",
    category: "core",
    status: "shipping",
    accent: "Sr",
    availableOn: ["cloud", "extension", "desktop"],
    featuresShipping: [
      "A crawled-web search engine — ask a question, get back ranked web results",
      "API endpoint for agents and LLMs — programmatic access with structured responses, built for automation",
      "Results grouped by meaning — clusters similar findings so patterns surface",
      "Natural language queries — ask like you'd ask a person; intent is classified, not just keyword-matched",
      "Instant previews — hover over a result to see context without leaving the search page",
    ],
    featuresPlanned: [
      "Cross-workspace search — one bar over your threads, files, research notes and code",
    ],
  },
  {
    slug: "dash",
    seoTitle: "Admin and observability",
    name: "Dash",
    domain: "dash.askfinz.ai",
    tagline: "The internal admin and observability surface.",
    description:
      "Dash is the internal control panel for askFinz operators and account admins. Account-wide config, billing, model usage, audit trails and observability — not a public product.",
    category: "core",
    status: "shipping",
    accent: "Da",
    internal: true,
    availableOn: ["cloud"],
    featuresShipping: [
      "Account-admin and operator-only access — role-based permissions, audit logs for every config change",
      "Model usage and cost ledgers — see which models your team uses most, track spend per user and per workspace",
      "Real-time observability — monitor system health, API latency, error rates and throughput",
      "Billing and plan administration — manage seats, upgrade plans, view invoices and payment history",
      "Audit trails for compliance — every action logged with timestamp, user and context for security reviews",
      "User and team management — invite users, assign roles, manage workspace access from one dashboard",
    ],
  },

  // ─── Tools (the plumbing) ───────────────────────────────────────────────
  {
    slug: "storage",
    seoTitle: "AI cloud drive: file storage that organises itself",
    name: "Storage",
    domain: "storage.askfinz.ai",
    tagline: "Your files, organised.",
    description:
      "Drag, drop, share and recover. A recycle bin, link sharing and quick search over your files.",
    category: "tools",
    status: "shipping",
    accent: "St",
    availableOn: ["cloud", "extension", "desktop"],
    featuresShipping: [
      "Drag and drop from anywhere — upload from your desktop, files are instantly available",
      "Conflict resolution that makes sense — when a name clashes, choose to replace or keep both",
      "File recovery — deleted by mistake? Restore it from the recycle bin, even weeks later",
      "Granular sharing controls — share a link with a password and an expiration date",
      "Search your files by name — find what you need from one search bar",
    ],
    featuresPlanned: [
      "Deep workspace integration — files from Chat and edits from Code syncing automatically",
      "Full version history, content search, download limits and smart auto-folders",
    ],
  },
  {
    slug: "train",
    seoTitle: "Fine-tune AI models on your own data",
    name: "Train",
    domain: "train.askfinz.ai",
    tagline: "Fine-tunes, tracked end to end.",
    description:
      "Run a fine-tune, watch the progress, ship the result. Roll back when you need to. Adapt a base model on your own data and deploy it next to the catalog.",
    category: "vertical", // re-categorised 2026-05: specialist ML workspace (kept in sync with the shared sidebar)
    status: "beta",
    accent: "Tr",
    availableOn: ["cloud"],
    featuresShipping: [
      "Fine-tune base models on your data — upload training examples, pick a base model, start a run",
      "Real-time training progress — watch loss curves, validation metrics and estimated time to completion",
      "Cost estimation before you commit — see what a training run will cost based on dataset size and model choice",
      "Speed tiers you control — pick fast (expensive) or slow (cheap) training based on your timeline",
      "One-click deployment — ship a trained model to production, it appears in Chat and other workspaces immediately",
      "Rollback and versioning — unship a model that's not performing, revert to an earlier checkpoint",
      "Training data stays private — your examples never leave your account, models train in isolated environments",
    ],
  },
  {
    slug: "workflow",
    seoTitle: "AI workflow automation: drag, drop, automate",
    name: "Workflow",
    domain: "workflow.askfinz.ai",
    tagline: "Drag, drop, automate.",
    description:
      "A visual pipeline builder. Wire askFinz into the way you already work — triggers, steps, AI calls, retries. Pipelines that run while you sleep.",
    category: "vertical", // re-categorised 2026-05: specialist automation workspace (kept in sync with the shared sidebar)
    status: "beta",
    accent: "Wf",
    availableOn: ["cloud", "desktop"],
    featuresShipping: [
      "Visual pipeline canvas — drag nodes, connect them, build automations without writing code",
      "Triggers from multiple sources — schedule, webhook, email, file upload or manual start",
      "AI steps that think — call Chat, Research or custom models mid-pipeline, use the output in the next step",
      "Retry logic and error handling — steps fail gracefully, retry with backoff, route errors to dead-letter queues",
      "External service integrations — connect to Slack, GitHub, Google Sheets, Airtable and dozens more with pre-built nodes",
      "Pipeline templates — start from proven patterns for common tasks like daily reports or data ingestion",
      "Execution history and logs — see every run, inspect inputs and outputs, debug failures step by step",
    ],
  },
  {
    slug: "mail",
    seoTitle: "AI inbox and calendar assistant",
    name: "Mail & Calendar",
    domain: "mail.askfinz.ai",
    tagline: "An inbox and a calendar with an agent on call.",
    description:
      "Email and calendar with an agent on call. Triage, drafts in your voice, and scheduling across calendars.",
    category: "tools",
    status: "shipping",
    accent: "Ma",
    availableOn: ["cloud", "extension", "desktop"],
    featuresShipping: [
      "Agent triage on every incoming thread — important threads surface first, the routine gets handled",
      "Drafts written in your voice — replies match your tone, ready to send or schedule",
      "Smart scheduling — find a time that works across calendars",
      "A real multi-account inbox + calendar — accounts, contacts, search and scheduled send",
    ],
    featuresPlanned: [
      "Calendar that drafts the agenda and summarises meetings when they're done",
      "Routing rules in plain English",
      "Inbox + calendar automations wired to Workflow",
    ],
  },
  {
    slug: "docs",
    seoTitle: "AI documentation: the manual, made readable",
    name: "Docs",
    domain: "docs.askfinz.ai",
    tagline: "The manual, but readable.",
    description: "How to use askFinz well, for the people who want to go deeper.",
    category: "tools",
    status: "soon",
    availableOn: ["cloud"],
    featuresPlanned: ["Walk-throughs", "How to ask better questions", "Workspace recipes"],
  },

  // ─── Verticals (specialist workspaces — most are roadmap) ───────────────
  {
    slug: "finance",
    seoTitle: "AI stock market terminal: live prices and charts",
    name: "Finance",
    domain: "finance.askfinz.ai",
    tagline: "The whole market, made clear.",
    description:
      "A global markets terminal — live prices and deep data on stocks, ETFs, crypto, indices and options worldwide, with AI forecasts and research, all explained in plain English.",
    category: "vertical",
    status: "shipping",
    availableOn: ["cloud", "desktop"],
    featuresShipping: [
      "Live prices and pro-grade charts for stocks, ETFs, crypto, indices and options across 50+ countries — one terminal for global markets",
      "Company financials, valuation, growth and per-share ratios, earnings-call transcripts and regulatory filings — the full picture on any name",
      "AI price forecasts with honest confidence ranges, plus options analytics and where to set a stop — read the risk before you act",
      "Screeners, watchlists and alerts that fire on the moves you care about — the market watches itself for you",
      "Portfolio and risk tools that show exposure and drawdown in plain numbers — know what you actually own",
      "Ask in plain English and get an answer grounded in the real figures and the actual earnings call — the terminal explains itself",
    ],
  },
  {
    slug: "med",
    seoTitle: "AI health assistant: a second pair of eyes",
    name: "Med",
    domain: "med.askfinz.ai",
    tagline: "A second pair of eyes for health.",
    description:
      "Organise records, look up references, prepare for appointments. Privacy isolated by design.",
    category: "vertical",
    status: "soon",
    featuresPlanned: [
      "A private, safe space for sensitive notes",
      "Quick reference for what the literature says",
      "Help getting ready for an appointment",
    ],
  },
  {
    slug: "learn",
    seoTitle: "AI tutor: turn your course into a tutor",
    name: "Learn",
    domain: "learn.askfinz.ai",
    tagline: "Your course, turned into a tutor.",
    description:
      "Bring in your real course material and Learn turns it into AI study tools, a lockdown exam simulator, a knowledge bank and honest progress analytics — grounded in your own lectures, explained in plain English.",
    category: "vertical",
    status: "shipping",
    availableOn: ["cloud", "extension", "desktop"],
    featuresShipping: [
      "Bring your courses in from Canvas, Brightspace, Moodle or Blackboard — lectures, slides and readings synced and read deeply, page by page",
      "Per-file AI study tools grounded in your own material — summaries, key concepts, quizzes, flashcards, true/false, a memory-match game and narrated mini-podcasts, all saved so nothing is regenerated",
      "Scope any tool to the pages that matter — mark the relevant slide ranges once and every summary, quiz and exam stays on-topic",
      "A lockdown exam simulator that writes itself from your course — timed, server-graded, with honest browser-level proctoring and an AI debrief on every attempt",
      "A knowledge bank that collects everything you've made — summaries, decks, podcasts and every exam question with its answer — per file and across the whole course",
      "Progress analytics that show your weak topics, exam readiness and a personal study plan — durable and cross-device, built from your real attempts",
    ],
    featuresPlanned: [
      "Spaced-repetition scheduling across your saved flashcards and missed questions",
      "Shared class study setups and collaborative resource libraries per course",
    ],
  },
  {
    slug: "data",
    seoTitle: "Talk to your spreadsheets: AI data analysis",
    name: "Data",
    domain: "data.askfinz.ai",
    tagline: "Talk to your spreadsheets.",
    description: "Drop a file, ask a question, get a chart. No formulas required.",
    category: "vertical",
    status: "soon",
    featuresPlanned: [
      "Drop a file, ask a question",
      "Charts written from a sentence",
      "Talk to your spreadsheet, get an answer",
    ],
  },
  {
    slug: "charts",
    seoTitle: "AI dashboard builder: charts without SQL",
    name: "Charts",
    domain: "charts.askfinz.ai",
    tagline: "Build your own dashboards.",
    description:
      "A self-serve dashboard and reporting workspace — connect your data, drag fields onto a canvas, build charts and share them. Like Power BI, native to askFinz.",
    category: "tools",
    status: "soon",
    accent: "Ch",
    availableOn: ["cloud"],
    featuresPlanned: [
      "Drag-and-drop dashboard builder — put metrics and charts on a canvas, no SQL required",
      "Connect your data — pull from your workspaces, files and spreadsheets",
      "A library of chart types — lines, bars, tables, KPIs, maps",
      "Share and embed dashboards — send a link, pin to a workspace, schedule a snapshot",
    ],
  },
  {
    slug: "knowledge",
    name: "Knowledge",
    domain: "knowledge.askfinz.ai",
    tagline: "All your tools, one source of truth.",
    description:
      "Connect the platforms you already use — Drive, SharePoint, Notion, Slack, GitHub, Jira, Salesforce and more — and askFinz pulls them into one searchable, permission-aware knowledge layer. Once it's in Knowledge, every other workspace can read it: ask in Chat, cite in Research, build a dashboard in Charts.",
    category: "tools",
    status: "soon",
    accent: "Kn",
    availableOn: ["cloud"],
    featuresPlanned: [
      "API integrations with 25+ platforms — Google Drive, OneDrive, SharePoint, Proton Drive, Dropbox, Box, Notion, Slack, Microsoft Teams, Zoom, GitHub, GitLab, Linear, Jira, Trello, Asana, Salesforce, Figma, SAP, Power BI, Shopify, BigCommerce, Zendesk, Airtable, SQL databases and CSV",
      "One searchable index across everything you've connected — find a file, a thread or a ticket without remembering which tool it lived in",
      "Permission-aware sync — your source-system access controls are honoured; nothing is exposed beyond what you can already see",
      "Available to the rest of askFinz — Chat, Research, Charts and the agents read from your Knowledge layer as a shared context source",
      "Incremental, scheduled re-sync so connected sources stay current",
    ],
  },
  {
    slug: "estate",
    seoTitle: "AI real estate: listings, projects and 3D models",
    name: "Estate",
    domain: "estate.askfinz.ai",
    tagline: "Properties, distilled.",
    description: "Listings with the questions you forgot to ask, answered upfront.",
    category: "vertical",
    /* WAS "beta" until 2026-09-06, and that overstated it. estate.askfinz.ai is a ~91-line
       placeholder — an AppShell wrapping a single card — not a product with a beta in it. Two
       independent content audits caught the same thing from opposite directions: the /apps/estate
       page simultaneously claimed "Estate is in beta... features are live" AND "will land at
       estate.askfinz.ai". `beta` here sits it beside Travel, Shop and Stream, which are real
       applications with dozens of routes each. Move it back to "beta" the day the app ships. */
    status: "soon",
    featuresPlanned: [
      "Property listings",
      "Decision-grade detail views",
    ],
  },
  {
    slug: "travel",
    seoTitle: "AI trip planner and booking",
    name: "Travel",
    domain: "travel.askfinz.ai",
    tagline: "Plan a trip in a paragraph. Book it without leaving the page.",
    description:
      "Flights, hotels, cars and activities — drafted, costed, booked, all inside one workspace. " +
      "Live carrier inventory, transparent pricing, multi-stage round-trip selection, heatmap seat picker, " +
      "tight-connection warnings and a discovery map of where you can fly from your home airport this year.",
    category: "vertical",
    status: "beta",
    featuresShipping: [
      "Flight search with multi-stage round-trip selection (outbound first, then matching returns)",
      "Live carrier inventory (~500 airlines) with self-transfer detection and full fare-rule + baggage display",
      "Heatmap seat picker — colour seats by cost across every segment of the itinerary",
      "Tight-layover warnings (<60 min international transfers flagged before booking)",
      "Hotel search with multi-source fallback — transparent per-night + tax breakdown, photos, reviews, amenities filter",
      "Car rental search end-to-end (search → quote → book → cancel)",
      "Activities + experiences pulled from a multi-source partner network and merged into one ranked list",
      "Price-discovery map on empty state — see the cheapest fare to ~10 popular cities from your origin",
      "Live ADS-B airport pulse per segment — see whether your connecting airport is actually moving traffic",
      "Cabin-class upgrade options surfaced on the offer page with price delta",
      "Real airport autocomplete with cross-app shared catalogue + IATA lookup",
      "Full cost breakdown with itemised taxes, per-traveler split, and transparent service fee",
      "Webhook ingestion for schedule changes + IROP; affected bookings flagged for review",
    ],
    featuresPlanned: [
      "Trip protection — quote and issue travel insurance in the offer flow (API wired; UI next)",
      "Restaurant + venue reservations embedded in-page",
      "Loyalty programme + cashback configurable per booking class",
      "Direct-to-airline NDC additions for live-mode coverage gaps",
    ],
  },
  {
    slug: "stream",
    seoTitle: "Video, live streaming and music",
    name: "Stream",
    domain: "stream.askfinz.ai",
    tagline: "Upload, go live, and earn from your audience.",
    description:
      "A creator media platform — upload video and music, go live from OBS, and earn from your audience in credits, with Finz AI assisting hosts and viewers throughout.",
    category: "vertical",
    status: "beta",
    featuresShipping: [
      "Upload video in any format — transcoded to adaptive, encrypted streaming that plays instantly",
      "Go live from OBS over RTMP — viewers watch with low latency; the broadcast saves as a VOD when you stop",
      "Music: upload tracks, build albums, get an artist profile with its own page and a global player",
      "Series, shows and playlists — group videos into episodes with Netflix-style next-episode autoplay",
      "Creator economy, all in credits — tips, channel subscriptions, a marketplace, pay-per-view events and collaboration splits",
      "Finz AI on every video — transcript-grounded summaries, ask-about-this-video, auto-detected highlight clips and shoppable product moments",
      "Channel + artist personalisation — banner, logo, about and links, with performance analytics dashboards",
      "Threaded comments, likes, follows and an in-app notifications inbox",
    ],
    featuresPlanned: [
      "Co-streaming and guest invites",
      "Channel memberships and tiers",
      "Cash-out — convert earned credits to your bank",
    ],
  },
  {
    slug: "shop",
    seoTitle: "AI storefront built into your workspace",
    name: "Shop",
    domain: "shop.askfinz.ai",
    tagline: "A storefront, native to the platform.",
    description:
      "An e-commerce workspace where anyone can spin up a storefront — products, checkout, payments, orders, analytics. The order pipeline is wired to Mail and Workflow from day one, so receipts, shipping events and returns are built-in, not bolted on.",
    category: "vertical",
    status: "beta",
    accent: "Sh",
    availableOn: ["cloud", "desktop"],
    featuresShipping: [
      "Spin up a storefront in minutes — pick a theme, drop in products, share the link, point a custom domain",
      "Catalog and inventory management — variants, stock levels, suppliers, restock alerts, supplier purchase orders",
      "Checkout that just works — cards, digital wallets, regional payment methods, taxes computed per locale",
      "Order pipeline wired to Mail and Workflow — confirmations, shipping events, return labels, refund flows",
      "Storefront analytics in plain English — best-sellers, conversion, cart abandonment, AOV, repeat-buyer cohorts",
      "Plain-text agent for support — answers customer questions about orders, returns and shipping with full context",
      "Asset library shared with Storage — every product image, every render, in one place",
      `Generate carrier shipping labels through the platform — live rates across ${CARRIER_TOTAL} carriers worldwide (USPS, UPS, FedEx, DHL, Royal Mail, PostNL, Aramex, Delhivery and more)`,
      "Automatic tracking that drives order status, plus return labels tied to refunds and a global tracking layer across postal networks",
      "Cross-border shipping with customs documents — HS codes, commercial invoice and a duties-and-taxes estimate shown to the buyer at checkout",
    ],
  },
  {
    slug: "legal",
    seoTitle: "AI legal research: case law and contracts",
    name: "Legal",
    domain: "legal.askfinz.ai",
    tagline: "Case law, contracts, in plain English.",
    description:
      "A workspace for studying case law and drafting and managing contracts. Notebooks that read, summarise and cite. A contract studio with a clause library that knows what your firm calls standard, and a risk panel that surfaces obligations the moment a document loads.",
    category: "vertical",
    status: "soon",
    accent: "Lg",
    availableOn: ["cloud", "desktop"],
    featuresPlanned: [
      "Search case law with citations that hold up — every claim links back to the judgment paragraph, not a paraphrase",
      "Contract studio with clause library — draft from templates, redline against your firm's baseline, accept/reject by clause",
      "Risk + obligation extraction — surface auto-renewal dates, uncapped indemnities, exclusive jurisdictions on document load",
      "Matter folders that talk to Storage and Mail — every doc, email, note and redline in one place per matter",
      "Plain-English explanations beside the legal text — read fast, dig in when you need to",
      "Signature workflow — route, sign, store, retrieve; full audit trail per signatory",
      "Conflict and citation checker — flag jurisdictions, opposing precedents, superseded rulings before you file",
    ],
  },
  {
    slug: "map",
    seoTitle: "Live flight, ship and satellite map",
    name: "Map",
    domain: "map.askfinz.ai",
    tagline: "Live aircraft, ships, satellites + every layer your trip needs.",
    description:
      "A real-time map service for every workspace and a destination-grade app in its own right. " +
      "Live ADS-B aircraft, AIS vessels, ~10k satellites tracked client-side, weather, airports, " +
      "ocean / pollution / events overlays, plus geocoding and tile services every other askFinz " +
      "workspace reaches into when it needs a pin, a route or a polygon.",
    category: "tools",
    status: "shipping",
    accent: "Mp",
    availableOn: ["cloud", "extension", "desktop"],
    featuresShipping: [
      "Live ADS-B aircraft tracking — pulled every 90s, classified into arrivals / departures / overflights / on-ground per airport",
      "Live AIS vessel tracking from multiple feeds merged into a shared vessels store, resilient to any one source going dark",
      "~10,000 satellites — TLE catalogue with a 2h cache and client-side SGP4 propagation; click for full orbital elements",
      "Airport activity API — recent arrivals/departures within ~35 km of any airport, used by the Travel offer page for live transfer-airport pulse",
      "Weather layer with current + 5-day forecast at any lat/lng",
      "13+ overlay layers — pollution, oceans, rail networks, events, reference maps, transit",
      "Geocoding + reverse geocoding for cross-workspace consumption (Estate listings, Travel routes, Projects teams)",
      "Tile layers and disk-cached map style proxy — streets, satellite, terrain, transit",
      "Map widget every workspace embeds — TripMap powers Travel's itinerary view + price-discovery map directly",
      "Multi-source data architecture — env-gated parallel ingest, NOAA bulk feeds, airport-activity derived from ADS-B hypertable",
    ],
    featuresPlanned: [
      "Offline-friendly tile cache — pre-download a region, the map keeps working without a connection",
      "Routing and isochrones — drive / walking / transit time polygons",
      "Batch API for agents — bulk geocode, bulk route, bulk reverse",
    ],
  },
  {
    slug: "create",
    seoTitle: "AI image and video studio",
    name: "Create",
    domain: "create.askfinz.ai",
    tagline: "A studio for the rendering, not just the prompting.",
    description:
      "An AI studio for generating, editing and remixing images and video. A layered editor for non-destructive image work, a timeline editor for video, an asset library shared with Storage, and a cost dial visible at every step.",
    category: "core", // re-categorised 2026-05: AI image/video is an everyday surface (kept in sync with the shared sidebar)
    status: "beta",
    accent: "Cr",
    availableOn: ["cloud", "desktop"],
    featuresShipping: [
      "Generate images and videos from a prompt — pick a frontier or fast model, control style, aspect ratio and seed",
      "Layered editor for images — masks, inpainting, outpainting, non-destructive history, infinite undo",
      "Timeline editor for video — trim, transitions, captions, audio tracks, B-roll generation per section",
      "Asset library shared with Storage — every render saved, tagged, organised, reusable across workspaces",
      "Cost and time estimate before you commit — credits and seconds shown for each render, not after",
      "Reference-image conditioning — drop in a style reference, a pose reference, a colour palette",
      "Multi-render batch — produce 12 variations of the same scene, scrub through, keep the winners",
    ],
  },
  {
    slug: "project",
    seoTitle: "AI project management: boards, tickets, sprints",
    name: "Projects",
    domain: "project.askfinz.ai",
    tagline: "A project tool that knows the rest of your work.",
    description:
      "Projects, sprints, boards and issues — with dependencies, burndown, automations and cross-project roll-ups.",
    category: "tools",
    status: "shipping",
    accent: "Pr",
    availableOn: ["cloud", "desktop"],
    featuresShipping: [
      "Boards, backlog and sprints — kanban for steady-state, scrum for time-boxed, or just a list",
      "Issue + incident links, bidirectional — connect related work, with a visual blocker view",
      "Dependencies that visualise — see what's blocking what, drag to reorder, surface critical path",
      "Burndown and velocity charts — computed from real activity, not story-point fiction",
      "Automations — auto-assign, notify, escalate and open sub-tasks on a rules engine + schedule",
      "Cross-project roll-ups — portfolio view across boards, with shared milestones and aggregate burndown",
    ],
    featuresPlanned: [
      "Tickets linked to commits in Code, threads in Chat, docs and emails",
      "Recurring work — repeating tickets, scheduled retrospectives, weekly status roll-ups",
    ],
  },
  {
    slug: "bank",
    seoTitle: "AI personal finance: every bank account in one place",
    name: "Bank",
    domain: "bank.askfinz.ai",
    tagline: "Your money, every account, one place.",
    description:
      "Your day-to-day money workspace. Connect every account, see balances, move money, pay bills, watch your budget. Distinct from Finance (which is for markets and portfolios) — Bank is for the income, the bills, the transfers, the budgets. Read-only by default, never on-sells your data.",
    category: "vertical",
    status: "soon",
    accent: "Bk",
    availableOn: ["cloud", "extension", "desktop"],
    featuresPlanned: [
      "Connect bank, credit and investment accounts in one place — balances and transactions kept current automatically",
      "Move money between accounts, set up recurring transfers, schedule one-off payments — opt-in write access per account",
      "Pay bills from inside the workspace — track due dates, automate the routine ones, snooze the ones in dispute",
      "Budgets that learn — calibrated to how you actually spend, with nudges before you overshoot",
      "Plain-English statements and alerts — no banking jargon, no notifications you didn't ask for",
      "Categorisation you can correct once — re-categorise a merchant and Bank learns it forever",
      "Tax-time summary — annual roll-ups by category, exportable in formats your accountant accepts",
    ],
  },
  {
    slug: "socials",
    seoTitle: "Social media scheduling and analytics",
    name: "Socials",
    domain: "socials.askfinz.ai",
    tagline: "Multi-account posting, scheduling and analysis.",
    description:
      "Connect every account, schedule across networks, analyse what's landing. One queue for posts, one calendar, one place to see how a thread travelled.",
    category: "vertical",
    status: "beta",
    accent: "So",
    availableOn: ["cloud", "extension", "desktop"],
    featuresShipping: [
      "Connect multiple accounts per network — manage Twitter, LinkedIn, Mastodon and more from one dashboard",
      "Unified posting queue — write once, schedule to multiple networks, customize per platform before publishing",
      "Cross-network scheduling — see all your posts on one calendar, drag to reschedule, avoid posting conflicts",
      "Analytics that make sense — track reach, engagement and replies across networks, see which posts landed",
      "Thread templates — save posting patterns that work, reuse them for similar content",
      "Draft collaboration — share drafts with teammates, get feedback before scheduling",
      "Post history and search — find old posts by keyword or date, see what you said about a topic months ago",
    ],
  },

  // ─── Identity / access workspace ────────────────────────────────────────
  {
    slug: "feedback",
    seoTitle: "Product feedback and bug tracker",
    name: "Feedback",
    domain: "feedback.askfinz.ai",
    tagline: "Bug reports, feature requests, community votes.",
    description:
      "The public feedback portal for the askFinz platform. Report bugs with severity and reproduction steps, " +
      "request features with a why and examples, and vote on what the team should build next. " +
      "Staff triage issues in real time — status changes, comment threads and resolution banners are " +
      "visible to everyone the moment they happen.",
    category: "tools",
    status: "shipping",
    accent: "Fb",
    availableOn: ["cloud"],
    featuresShipping: [
      "Bug tracker with P0–P3 severity triage and a full status pipeline — open → in progress → awaiting verify → resolved",
      "Community voting on every issue and request — votes surface what matters most, in real time",
      "Feature request board with roadmap statuses — considering, planned, building, shipped",
      "Comment threads on every issue and request — staff replies and community discussion in one timeline",
      "Awaiting-verify banner prompts community members to retest a fix before it closes",
      "App-level filter chips — scope issues and requests to any single workspace instantly",
      "Staff-only internal visibility — items can be kept internal until ready to share publicly",
      "Live KPI cards — P0 open count, total open, awaiting retest, and resolved fixes shipped",
      "Hash-dedup on new submissions — the system detects near-identical reports before they're saved",
    ],
  },
  {
    slug: "access",
    seoTitle: "Single sign-on for every workspace",
    name: "Access",
    domain: "access.askfinz.ai",
    tagline: "One sign-in, every workspace, every device.",
    description:
      "Your identity layer for askFinz — single sign-on, sessions, billing, devices, recovery. Built to plug into askFinz OS so a fresh workstation comes online already signed in, with the workspaces, browser and extension already configured.",
    category: "tools",
    status: "beta",
    accent: "Ac",
    availableOn: ["cloud", "desktop"],
    featuresShipping: [
      "Single sign-on across every askFinz workspace — sign in once, every room opens for you",
      "Up to four identities per device — switch between work, personal and client accounts without signing out",
      "Session and device management — see where you're signed in, sign a device out remotely",
      "Recovery flows that actually work — passkeys, backup codes, trusted contacts, no support ticket required",
      "Billing and seats in one place — manage plans, invoices and team access from a single page",
      "Manages askFinz OS devices — SSH (cert-minted), NoVNC, RDP, Wake-on-LAN, deploy-from-repo, k3s + KubeVirt ops",
    ],
  },
];

export const APP_GROUPS: { id: "core" | "tools" | "vertical"; label: string; blurb: string }[] = [
  { id: "core",     label: "Core",      blurb: "The everyday surfaces — chat, research, code, news, search, dash." },
  { id: "tools",    label: "Tools",     blurb: "The plumbing — storage, training, automation, documentation." },
  { id: "vertical", label: "Verticals", blurb: "Purpose-built workspaces for finance, medicine, learning, life." },
];

export const STATUS_LABEL: Record<AppStatus, string> = {
  shipping: "Available",
  beta: "Beta",
  soon: "Coming soon",
};

/* ────────────────────────────────────────────────────────────────────────
   PLATFORM SURFACES BEYOND THE WEB APPS
   ──────────────────────────────────────────────────────────────────────── */

export type PlatformSurface = {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  status: AppStatus;
};

export const SURFACES: PlatformSurface[] = [
  {
    slug: "extension",
    name: "Browser extension",
    tagline: "askFinz, wherever you read.",
    description:
      "A side panel that brings askFinz to whatever you're reading, without leaving the page.",
    status: "shipping",
  },
  {
    slug: "desktop",
    name: "Desktop",
    tagline: "Off the browser tab.",
    description:
      "A desktop client for Windows, macOS and Linux that wraps your workspaces and works offline when you do.",
    status: "shipping",
  },
  {
    slug: "os",
    name: "askFinz OS",
    tagline: "Your hardware, managed from the cloud.",
    description:
      "A purpose-built Linux distro (Ubuntu 24.04 LTS + k3s + KubeVirt) for workstations, servers and IoT devices. Kiosk into the askFinz cloud on top; a managed compute node the cloud schedules workloads onto underneath.",
    status: "beta",
  },
];

/* ────────────────────────────────────────────────────────────────────────
   PLATFORM-LEVEL FACTS
   Verified against shared_backend code + docs/. Sources noted inline.
   ──────────────────────────────────────────────────────────────────────── */

// Verified shipping web workspaces (see status: "shipping" or "beta" above).
const shippingApps = APPS.filter((a) => a.status !== "soon").length;

// Import agent count from the actual roster
import { totalAgentCount } from "./agent-roster";

export const STATS = [
  { value: shippingApps, suffix: "+", label: "Live workspaces" },
  { value: totalAgentCount(), suffix: "+",  label: "Agents" },
  { value: 1,  suffix: "",  label: "Unified login" },
  { value: 0,  suffix: "",  label: "Model lock-in" },
];

export const PROVIDERS = [
  { name: "OpenAI",     slug: "openai",     logoLight: "/shared/ai_models/open-ai_lightmode.png", logoDark: "/shared/ai_models/open-ai_darkmode.png" },
  { name: "Gemini",     slug: "gemini",     logoLight: "/shared/ai_models/gemini.png",            logoDark: "/shared/ai_models/gemini.png" },
  { name: "Mistral",    slug: "mistral",    logoLight: "/shared/ai_models/mistral.png",           logoDark: "/shared/ai_models/mistral.png" },
  { name: "Llama",      slug: "llama",      logoLight: "/shared/ai_models/llama.png",             logoDark: "/shared/ai_models/llama.png" },
  { name: "Qwen",       slug: "qwen",       logoLight: "/shared/ai_models/qwen.png",              logoDark: "/shared/ai_models/qwen.png" },
  { name: "DeepSeek",   slug: "deepseek",   logoLight: "/shared/ai_models/deepseek.png",          logoDark: "/shared/ai_models/deepseek.png" },
  { name: "Moonshot",   slug: "moonshot",   logoLight: "/shared/ai_models/moonshot.png",          logoDark: "/shared/ai_models/moonshot.png" },
  { name: "NVIDIA NIM", slug: "nvidia",     logoLight: "/shared/ai_models/nvidia.png",            logoDark: "/shared/ai_models/nvidia.png" },
  { name: "Z.ai",       slug: "zai",        logoLight: "/shared/ai_models/zai_lightmode.png",     logoDark: "/shared/ai_models/zai_darkmode.png" },
  { name: "Google Cloud", slug: "gcp",      logoLight: "/shared/ai_models/gcp.png",               logoDark: "/shared/ai_models/gcp.png" },
];

/**
 * On-device model catalogue served by the browser extension's Model Hub
 * (the `askfinz_extension` container at extension.askfinz.ai / 127.0.0.1:2027).
 * These are open-weight MLC/WebLLM models that run LOCALLY in the browser —
 * entirely distinct from PROVIDERS above (the cloud routing set). The live hub
 * catalogue is ~383 today and drifts as models are added and hardware-filtered
 * per device, so we surface a soft rounded floor (rendered as "380+"). Bump
 * this single constant occasionally; never hardcode the number in a page.
 */
export const LOCAL_MODEL_COUNT = 380;

/**
 * FLOOR for the number of collections in the index — used only where a live read is impossible.
 *
 * READ THIS BEFORE CHANGING IT. This number must never be edited to "the current count". It is a
 * conservative floor that the live figure is expected to exceed, and its whole job is to be wrong
 * in the SAFE direction when `getCollectionCount()` returns null (index restarting, build-time
 * render, upstream timeout).
 *
 * It has now drifted twice in opposite directions, which is why the live read exists:
 *   - it sat at 44 after the real figure had passed it, understating the index;
 *   - it was then raised to 51 and OVERSHOT — /live/storage reported 49 physical collections, two
 *     of which are askFinz's own site index rather than a library of the web, so the true
 *     public-facing figure was 47 and the site was claiming four collections it did not have.
 *
 * Anywhere that CAN await, use `getCollectionCount()` from lib/live-stats.ts instead of this.
 * Route handlers and page bodies can. Next `metadata` exports cannot, which is the only reason
 * this constant is still referenced anywhere.
 */
export const COLLECTION_COUNT = 45;

/** Vector stores in the fleet. This is the DEPLOYED count (`instances_ok` on /live/storage), not
 *  the number currently holding a collection — 20 run, 17 hold indexed collections today. Both are
 *  true; copy on /collections means the fleet. `getStoreCount()` returns the narrower figure. */
export const STORE_COUNT = 20;

export const AGENTS = [
  {
    slug: "researcher",
    name: "Researcher",
    role: "Investigates, synthesises and cites.",
    description:
      "Reads broadly, summarises faithfully and never claims a fact it can't reference. Powers the Research workspace.",
  },
  {
    slug: "coder",
    name: "Coder",
    role: "Pairs on engineering work.",
    description:
      "Writes, reviews, refactors and runs code. Splits a big task into pieces it can hand back to you one at a time. Remembers project context.",
  },
  {
    slug: "navigator",
    name: "Navigator",
    role: "Operates the live web.",
    description:
      "Drives a real browser to fill forms, extract content, complete sessions. You can watch it work, frame by frame.",
  },
];

export const CAPABILITIES = [
  {
    title: "Multi-model, one mind.",
    body: "Route any question to the right model — automatically. Switch by hand whenever you like.",
    badge: "Models",
  },
  {
    title: "Agents that finish the job.",
    body: "An agent for research, an agent for code, an agent for the live web. Composable. Persistent. Patient.",
    badge: "Agents",
  },
  {
    title: "A workspace per discipline.",
    body: "Chat, research, code, finance, medicine, learning. Each its own room, all sharing one memory.",
    badge: "Workspaces",
  },
  {
    title: "Cite the source. Always.",
    body: "Research mode never makes a claim it can't back. Footnotes are first-class.",
    badge: "Trust",
  },
  {
    title: "Run a browser, watch it work.",
    body: "The Navigator drives a real browser and streams it back to you. You stay in the loop.",
    badge: "Web",
  },
  {
    title: "Your data, your perimeter.",
    body: "Sensitive workspaces have their own database with isolated credentials and encryption.",
    badge: "Privacy",
  },
];
